/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Dashboard v1.0.1 - Componente Principal Completo
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file WaspDashboard.tsx
 * @description Dashboard principal de WASP con todos los tabs integrados
 * @version 1.0.1 - CORREGIDO (Race condition fix)
 * @architecture Frontend → Proxy (backend.anubisai.net) → WASP (192.168.112.3)
 * 
 * TABS IMPLEMENTADOS:
 * 1. ✅ Configuración - Control de switches y estado del sistema
 * 2. ✅ Conversaciones - Sistema completo de chat (NUEVO)
 * 3. ✅ Gestión de Flujos - Directivas estáticas y dinámicas
 * 4. ✅ Configuración IA - CoPilot prompts y configuración
 * 5. ✅ Aplicaciones - Placeholder para funcionalidades futuras (NUEVO)
 * 
 * 🔧 CORRECCIÓN APLICADA:
 * - fetchWaspStatus() línea ~236: Agregado check de loadingToggles (4 líneas)
 * - TODO LO DEMÁS PERMANECE INTACTO
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Tab, Nav, Button, Card, Col, Form, Row, Modal, Spinner, Alert, Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCircleDot, 
  faQrcode,
  faCog,
  faComments,
  faRobot,
  faBrain,
  faRocket,
  faStream,
  faChartLine,
  faSync,
  faLink,
  faSignOutAlt,
  faBroadcastTower,
  faNetworkWired,
  faLock,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { useUserContext } from '../../layouts/UserContext';
import { getHatateToken } from '../../helpers/utils';
import { IUser } from '../../data/models/user/IUser';
import wasplogo from '../../assets/img/Anubis/WASP.png';

// ═══════════════════════════════════════════════════════════════════════════
// IMPORTS DE COMPONENTES DE TABS
// ═══════════════════════════════════════════════════════════════════════════

// Tab de Conversaciones (NUEVO)
import ConversationsTable from '../../layouts/ConversationsTable';

// Tab de Dashboard Stats (NUEVO)
import DashboardStatsTab from '../../components/DashboardStatsTab';

// Tab de Gestión de Flujos
import DirectivesTable from '../../layouts/DirectivesTable';

// Tab de Configuración IA
import DirectivesCopilot from '../../layouts/CoPilotPromptsTable';

// Tab de Aplicaciones (NUEVO - Placeholder)
import ApplicationsTab from '../../layouts/ApplicationsTab';

// Tab de Configuración (NUEVO - Placeholder)

import ConfigurationTab from '../../layouts/ConfigurationTab';
import { useNavigate } from 'react-router-dom';

// Componentes adicionales (opcionales, descomentar si existen)
// import JoinGroupComponent from '../components/JoinGroupComponent';
// import ImageAnalysisComponent from '../components/ImageAnalysisComponent';
// import FileUploadProcessor from './FileUploadProcessor';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

type LanguageType = 'es' | 'en';

interface WaspStatus {
  success: boolean;
  botEnabled: boolean;
  whatsappConnected?: boolean;
  dynamicFlowsEnabled: boolean;
  isCoPilotEnabled: boolean;
  provider: string;
  flowsCount: number;
  timestamp: string;
  
  // Nuevos campos del sistema de flags
  assistantEnabled?: boolean;
}

interface ServiceIndicator {
  name: string;
  status: 'online' | 'offline' | 'checking';
  icon: any;
  color: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Verifica si el usuario tiene una característica de WhatsApp habilitada
 * 
 * @TODO: REMOVER MOCK - Esta función debe leer de user.whatsappInfo.features
 * Los features deben ser configurados en el backend para cada usuario.
 * 
 * Features disponibles:
 * - 'whatsapp-assistant': Permite usar Switch 1 (Bot básico)
 * - 'automatic-response-directives': Permite usar Switch 2 (Flows dinámicos)
 * - 'copilot-mode': Permite usar Switch 3 (IA/CoPilot)
 * 
 * Mock temporal para desarrollo (retorna true para todos los features)
 */
const hasWsFeatureEnabled = (user: IUser, feature: string): boolean => {
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔴 MOCK TEMPORAL - REMOVER EN PRODUCCIÓN
  // ═══════════════════════════════════════════════════════════════════════════
  // TODO: Descomentar esta línea cuando el backend configure los features:
  // if (!user || !user.whatsappInfo) return false;
  // return user.whatsappInfo.features.includes(feature);
  
  // Mock: Permitir todos los features durante desarrollo
  console.log(`[MOCK] Feature check: ${feature} → true (mock)`);
  return true;
  
  // ═══════════════════════════════════════════════════════════════════════════
};

/**
 * Sistema de Cache para Estado del Dashboard
 * - Persiste estado del sistema en localStorage
 * - TTL: 30 segundos (más corto que temperaturas porque es crítico)
 * - Carga instantánea en mount
 */
class DashboardCache {
  private static CACHE_KEY = 'wasp_dashboard_status';
  private static CACHE_TTL = 30000; // 30 segundos

  static set(data: {
    isWhatsAppEnabled: boolean;
    isDirectivesEnabled: boolean;
    isCoPilotEnabled: boolean;
    whatsappSocketStatus: string;
    isQRAvailable: boolean;
    isEndpointAvailable: boolean;
    isPM2Running: boolean;
    isMLAvailable: boolean;
    mlProvider: string;
  }): void {
    try {
      const cache = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('[CACHE] Error saving dashboard status:', error);
    }
  }

  static get(): any | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cache = JSON.parse(cached);
      const age = Date.now() - cache.timestamp;

      if (age > this.CACHE_TTL) {
        this.clear();
        return null;
      }

      return cache.data;
    } catch (error) {
      console.warn('[CACHE] Error reading dashboard status:', error);
      return null;
    }
  }

  static clear(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.warn('[CACHE] Error clearing cache:', error);
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const WaspDashboard: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // CONTEXT & USER DATA
  // ───────────────────────────────────────────────────────────────────────
  
  const { user } = useUserContext();
  const API_TOKEN_KEY = getHatateToken();
  
  // URLs base
  const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
  const ML_API_URL = 'https://backend.anubisai.net/api/mybrain/ml/status';
  const API_KEY = 'anubis-whatsapp-api-key';

  // ───────────────────────────────────────────────────────────────────────
  // STATE - TAB NAVIGATION
  // ───────────────────────────────────────────────────────────────────────
  
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const navigate = useNavigate();
  // ───────────────────────────────────────────────────────────────────────
  // STATE - CONFIGURACIÓN DEL BOT (SWITCHES)
  // ───────────────────────────────────────────────────────────────────────
  
  const [isWhatsAppEnabled, setIsWhatsAppEnabled] = useState(false);
  const [isDirectivesEnabled, setIsDirectivesEnabled] = useState(false);
  const [isCoPilotEnabled, setIsCoPilotEnabled] = useState(false);
  
  const [whatsappSocketStatus, setWhatsappSocketStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const [isQRAvailable, setIsQRAvailable] = useState(false);
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE - INDICADORES DE SERVICIOS
  // ───────────────────────────────────────────────────────────────────────
  
  const [isEndpointAvailable, setIsEndpointAvailable] = useState(false);
  const [isPM2Running, setIsPM2Running] = useState(false);
  const [isMLAvailable, setIsMLAvailable] = useState(false);
  const [mlProvider, setMlProvider] = useState<string>('');

  // ───────────────────────────────────────────────────────────────────────
  // STATE - UI & LOADING
  // ───────────────────────────────────────────────────────────────────────
  
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingToggles, setLoadingToggles] = useState({
    whatsapp: false,
    directives: false,
    copilot: false
  });
  
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrImageData, setQrImageData] = useState<string | null>(null);
  const [qrCountdown, setQrCountdown] = useState(15);
  
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutCode, setLogoutCode] = useState<string>('');
  // Estados para QR mejorado
  const [qrStatus, setQrStatus] = useState<'loading' | 'ready' | 'scanned' | 'expired'>('loading');
  const [qrAutoCloseTimer, setQrAutoCloseTimer] = useState<number>(30);

  const [userInputCode, setUserInputCode] = useState<string>('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // REFS
  // ───────────────────────────────────────────────────────────────────────
  
  const qrModalTimerRef = useRef<NodeJS.Timeout | null>(null);
  const qrUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSystemBlocked = whatsappSocketStatus === 'disconnected' && !isEndpointAvailable;

  // ───────────────────────────────────────────────────────────────────────
  // EFECTOS - INICIALIZACIÓN Y LIMPIEZA
  // ───────────────────────────────────────────────────────────────────────

// ✅ Inicializar desde cache (INSTANTÁNEO)
  useEffect(() => {
    if (user) {
      const cached = DashboardCache.get();
      if (cached) {
        console.log('[CACHE] Loading dashboard status from cache');
        setIsWhatsAppEnabled(cached.isWhatsAppEnabled);
        setIsDirectivesEnabled(cached.isDirectivesEnabled);
        setIsCoPilotEnabled(cached.isCoPilotEnabled);
        setWhatsappSocketStatus(cached.whatsappSocketStatus);
        setIsQRAvailable(cached.isQRAvailable);
        setIsEndpointAvailable(cached.isEndpointAvailable);
        setIsPM2Running(cached.isPM2Running);
        setIsMLAvailable(cached.isMLAvailable);
        setMlProvider(cached.mlProvider);
      }
    }
  }, [user]);

useEffect(() => {
    if (user) {
      // ✅ Primera carga sin await (no bloquea)
      fetchWaspStatus().catch(err => {
        console.error('[WASP] Initial status fetch failed:', err);
      });
      
      // Check status cada 10 segundos
      statusCheckIntervalRef.current = setInterval(() => {
        // ✅ Sin await, no bloquea
        fetchWaspStatus().catch(err => {
          console.error('[WASP] Periodic status fetch failed:', err);
        });
      }, 10000);
    }

    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
      if (qrModalTimerRef.current) {
        clearTimeout(qrModalTimerRef.current);
      }
      if (qrUpdateIntervalRef.current) {
        clearInterval(qrUpdateIntervalRef.current);
      }
    };
  }, [user]);

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES - ESTADO DEL SISTEMA
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Obtener estado completo del sistema WASP
   * 🔧 CORREGIDO: Agregado check de loadingToggles para prevenir race condition
   */
  const fetchWaspStatus = async () => {
    if (!user) return;
    
    // 🔧 Prevenir sobrescritura durante operaciones de toggle
    if (loadingToggles.whatsapp || loadingToggles.directives || loadingToggles.copilot) {
      console.log('[WASP] ⏸️ Saltando auto-refresh: toggle en progreso');
      return;
    }
    
    setLoadingStatus(true);
    setErrorMessage(null);
    
    try {
      // ✅ EJECUTAR LAS 3 LLAMADAS EN PARALELO
      const [statusResult, pm2Result, mlResult] = await Promise.allSettled([
        // 1. Estado del bot
        axios.get<WaspStatus>(`${API_BASE_URL}/status`, {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': API_TOKEN_KEY
          },
          timeout: 10000  // ✅ Reducido de 30s a 10s
        }),
        
        // 2. Estado PM2
        axios.get(`${API_BASE_URL}/pm2status`, {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': API_TOKEN_KEY
          },
          timeout: 5000  // ✅ Reducido de 10s a 5s
        }),
        
        // 3. Estado ML Service
        axios.get(ML_API_URL, {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': API_TOKEN_KEY
          },
          timeout: 5000  // ✅ Reducido de 10s a 5s
        })
      ]);

      // ✅ Procesar resultado 1: Bot Status
      if (statusResult.status === 'fulfilled' && statusResult.value.data.success) {
        console.log('[WASP] Estado recibido:', statusResult.value.data);
        const status = statusResult.value.data;
        
        // Bot Logic
        setIsWhatsAppEnabled(status.assistantEnabled ?? status.botEnabled);
        setIsDirectivesEnabled(status.dynamicFlowsEnabled);
        setIsCoPilotEnabled(status.isCoPilotEnabled);
        
        // WhatsApp Socket Connection
        let isConnected = false;
        
        if (typeof status.whatsappConnected === 'boolean') {
          isConnected = status.whatsappConnected;
        } else {
          isConnected = 
            status.provider === 'connected' || 
            status.provider === 'online' || 
            status.provider === 'ready' ||
            status.provider === 'authenticated';
        }
          
        if (isConnected) {
          setWhatsappSocketStatus('connected');
          setIsQRAvailable(false);
        } else if (status.provider === 'qr' || status.provider === 'connecting') {
          setWhatsappSocketStatus('connecting');
          setIsQRAvailable(true);
        } else {
          setWhatsappSocketStatus('disconnected');
          setIsQRAvailable(true);
        }
        
        setIsEndpointAvailable(true);
      } else {
        console.warn('[WASP] Bot status failed:', statusResult);
        setIsEndpointAvailable(false);
      }

      // ✅ Procesar resultado 2: PM2 Status
      if (pm2Result.status === 'fulfilled' && pm2Result.value.data.success) {
        setIsPM2Running(pm2Result.value.data.running || pm2Result.value.data.status === 'online');
      } else {
        console.warn('[WASP] PM2 status failed:', pm2Result);
        setIsPM2Running(false);
      }

      // ✅ Procesar resultado 3: ML Status
      if (mlResult.status === 'fulfilled' && mlResult.value.data?.status) {
        const mlStatusValue = mlResult.value.data.status;
        setIsMLAvailable(mlStatusValue === 'running' || mlStatusValue === 'online');
        setMlProvider(mlResult.value.data.provider || mlStatusValue);
      } else {
        console.warn('[WASP] ML status failed:', mlResult);
        setIsMLAvailable(false);
        setMlProvider('');
      }

      // ✅ Guardar en cache
      DashboardCache.set({
        isWhatsAppEnabled,
        isDirectivesEnabled,
        isCoPilotEnabled,
        whatsappSocketStatus,
        isQRAvailable,
        isEndpointAvailable,
        isPM2Running,
        isMLAvailable,
        mlProvider
      });

    } catch (error: any) {
      console.error('[WASP] Error fetching WASP status:', error);
      setIsEndpointAvailable(false);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Token de autenticación inválido o expirado');
      } else {
        setErrorMessage('Error al comunicarse con el servicio WASP');
      }
    } finally {
      setLoadingStatus(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES - TOGGLE SWITCHES
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Toggle Switch 1: WhatsApp Assistant
   */
  const handleToggleWhatsApp = async () => {
    if (!hasWsFeatureEnabled(user, 'whatsapp-assistant')) {
      setErrorMessage('No tienes permiso para modificar WhatsApp Assistant');
      return;
    }

    const newState = !isWhatsAppEnabled;
    
    setLoadingToggles(prev => ({ ...prev, whatsapp: true }));
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/assistant/toggle`,
        { 
          enabled: newState,
          processName: `Wasp v1.0.1 ${user._id}`
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data.success) {
        console.log('[WASP] Bot toggled successfully:', response.data);
        
        // Actualizar estado local directamente con la respuesta del backend
        setIsWhatsAppEnabled(response.data.currentState);
        
        // Si desactivamos, cascada de desactivación
        if (!response.data.currentState) {
          setIsDirectivesEnabled(false);
          setIsCoPilotEnabled(false);
        }
        
        setSuccessMessage(`WhatsApp Assistant ${response.data.currentState ? 'activado' : 'desactivado'} correctamente`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Desbloquear después de 3 segundos para dar tiempo al backend a persistir
        setTimeout(() => {
          setLoadingToggles(prev => ({ ...prev, whatsapp: false }));
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Toggle failed');
      }
      
    } catch (error: any) {
      console.error('[WASP] Error toggling bot:', error);
      setErrorMessage(error.response?.data?.message || error.message);
      setLoadingToggles(prev => ({ ...prev, whatsapp: false }));
    }
  };

  /**
   * Toggle Switch 2: Directivas de Respuesta Automática
   */
  const handleToggleDirectives = async () => {
    if (!isWhatsAppEnabled) {
      setErrorMessage('Debes activar WhatsApp Assistant primero');
      return;
    }

    if (!hasWsFeatureEnabled(user, 'automatic-response-directives')) {
      setErrorMessage('No tienes permiso para modificar Directivas de Respuesta');
      return;
    }

    const newState = !isDirectivesEnabled;
    
    setLoadingToggles(prev => ({ ...prev, directives: true }));
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/flows/toggle-dynamic`,
        { 
          enabled: newState,
          processName: `Wasp v1.0.1 ${user._id}`
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data.success) {
        console.log('[WASP] Dynamic flows toggled:', response.data);
        
        // Actualizar estado local directamente con la respuesta del backend
        setIsDirectivesEnabled(response.data.currentState);
        
        // Si desactivamos directivas, desactivar CoPilot también
        if (!response.data.currentState && isCoPilotEnabled) {
          setIsCoPilotEnabled(false);
        }
        
        setSuccessMessage(`Directivas ${response.data.currentState ? 'activadas' : 'desactivadas'} correctamente`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Desbloquear después de 3 segundos para dar tiempo al backend a persistir
        setTimeout(() => {
          setLoadingToggles(prev => ({ ...prev, directives: false }));
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Toggle failed');
      }
      
    } catch (error: any) {
      console.error('[WASP] Error toggling directives:', error);
      setErrorMessage(error.response?.data?.message || error.message);
      setLoadingToggles(prev => ({ ...prev, directives: false }));
    }
  };

  /**
   * Toggle Switch 3: Modo CoPilot
   */
  const handleToggleCoPilot = async () => {
    if (!isWhatsAppEnabled || !isDirectivesEnabled) {
      setErrorMessage('Debes activar WhatsApp Assistant y Directivas primero');
      return;
    }

    if (!hasWsFeatureEnabled(user, 'copilot-mode')) {
      setErrorMessage('No tienes permiso para modificar Modo CoPilot');
      return;
    }

    const newState = !isCoPilotEnabled;
    
    setLoadingToggles(prev => ({ ...prev, copilot: true }));
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/copilot/toggle`,
        { 
          enabled: newState,
          processName: `Wasp v1.0.1 ${user._id}`
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data.success) {
        console.log('[WASP] CoPilot toggled:', response.data);
        
        // Actualizar estado local directamente con la respuesta del backend
        setIsCoPilotEnabled(response.data.currentState);
        
        setSuccessMessage(`Modo CoPilot ${response.data.currentState ? 'activado' : 'desactivado'} correctamente`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Desbloquear después de 3 segundos para dar tiempo al backend a persistir
        setTimeout(() => {
          setLoadingToggles(prev => ({ ...prev, copilot: false }));
        }, 3000);
      } else {
        throw new Error(response.data.message || 'CoPilot toggle failed');
      }
      
    } catch (error: any) {
      console.error('[WASP] Error toggling CoPilot:', error);
      setErrorMessage(error.response?.data?.message || error.message);
      setLoadingToggles(prev => ({ ...prev, copilot: false }));
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES - QR CODE
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Abrir modal de QR con autenticación
   */
  const handleOpenQR = () => {
    setErrorMessage(null);
    setShowQRModal(true);
    setQrCountdown(30);
    setQrAutoCloseTimer(30);
    setQrStatus('loading');
    
    // ✅ Cargar QR con autenticación usando axios
    const loadQRImage = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/qr.png`, {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': API_TOKEN_KEY
          },
          responseType: 'blob',
          timeout: 10000
        });

        // Convertir blob a data URL
        const imageUrl = URL.createObjectURL(response.data);
        setQrImageData(imageUrl);
        setQrStatus('ready');
      } catch (error) {
        console.error('[QR] Error cargando imagen:', error);
        setQrStatus('expired');
      }
    };

    loadQRImage();

    // Actualizar QR cada 3 segundos
    qrUpdateIntervalRef.current = setInterval(() => {
      loadQRImage();
    }, 3000);

    // Timer de auto-close (30 segundos)
    qrModalTimerRef.current = setTimeout(() => {
      handleCloseQR();
    }, 30000);
  };

  /**
   * Cerrar modal de QR
   */
  const handleCloseQR = () => {
    setShowQRModal(false);
    setQrImageData(null);
    
    // Limpiar todos los timers
    if (qrModalTimerRef.current) {
      clearTimeout(qrModalTimerRef.current);
      qrModalTimerRef.current = null;
    }
    
    if (qrUpdateIntervalRef.current) {
      clearInterval(qrUpdateIntervalRef.current);
      qrUpdateIntervalRef.current = null;
    }

    // Refrescar estado después de cerrar
    setTimeout(() => {
      fetchWaspStatus();
    }, 1000);
  };

  /**
   * Manejar logout del sistema
   */
  const handleLogout = async () => {
    // Generar código de 6 dígitos
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setLogoutCode(code);
    setUserInputCode('');
    setCodeError(null);
    setSendingCode(true);

    try {
      // Enviar código al número fijo (temporal)
      const response = await axios.post(
        `${API_BASE_URL}/send`,
        {
          to: '56981925986',
          message: `Se ha solicitado el cierre de sesión en tu servicio *WASP de red LINKHUB*.\n\n
---
🔑 *CÓDIGO DE SEGURIDAD Y CONFIRMACIÓN:*

# *${code}*

⚠️ *VIGENCIA:* Este código es válido por solo *2 minutos*.
---
🚨 *IMPLICACIONES CRÍTICAS (LEER ANTES DE USAR)* 🚨

El uso de este código confirma el cierre de sesión y:

1️⃣ *Pérdida de Sincronización:* Sus *mensajes NO estarán sincronizados* con el servicio LINKHUB.

2️⃣ *Aviso:* Esto puede afectar la continuidad de su comunicación.
---
🛡️ *¿NO SOLICITASTE ESTO?*

Si *NO fuiste tú* quien solicitó este cierre:

* *IGNORA* este mensaje y *NO* compartas el código.
* *IMPORTANTE:* Si ves actividad inusual, contacta a Soporte de inmediato.

*Tu entendimiento y acción/inacción son cruciales.*          
          `,
        },
        {
          headers: {
            'Authorization': API_TOKEN_KEY,
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );

      if (response.data.success) {
        console.log('[LOGOUT] ✅ Código enviado correctamente:', code);
        setShowLogoutModal(true);
        setSuccessMessage('Código enviado a WhatsApp');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('No se pudo enviar el código');
      }
    } catch (error: any) {
      console.error('[LOGOUT] ❌ Error enviando código:', error);
      setErrorMessage(error.response?.data?.message || 'Error al enviar código de verificación');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setSendingCode(false);
    }
  };

  /**
   * Verificar código ingresado por el usuario
   */
  const handleVerifyLogoutCode = async () => {
    if (!userInputCode.trim()) {
      setCodeError('Debes ingresar el código');
      return;
    }

    setVerifyingCode(true);
    setCodeError(null);

    // Simular verificación (pequeño delay para UX)
    await new Promise(resolve => setTimeout(resolve, 500));

    if (userInputCode.trim() === logoutCode) {
      // ✅ Código correcto
      setVerifyingCode(false);
      setShowLogoutModal(false);
      
      // 🔴 DEMO: Alert temporal
      alert('Respuesta OK. [no disponible]');
      
      // TODO: Implementar logout real cuando esté disponible
      // localStorage.removeItem('hatateToken');
      // sessionStorage.clear();
      // window.location.href = '/login';
      
    } else {
      // ❌ Código incorrecto
      setVerifyingCode(false);
      setCodeError('Código incorrecto. Verifica e intenta nuevamente.');
    }
  };

  /**
   * Cancelar proceso de logout
   */
  const handleCancelLogout = () => {
    setShowLogoutModal(false);
    setLogoutCode('');
    setUserInputCode('');
    setCodeError(null);
  };

  /**
   * Abrir LinkHub en nueva pestaña
   */
  const handleOpenLinkHub = () => {
    navigate('../linkhub');
  };

  // Countdown del QR modal
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (showQRModal && qrAutoCloseTimer > 0) {
      interval = setInterval(() => {
        setQrAutoCloseTimer(prev => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showQRModal, qrAutoCloseTimer]);

  // Detectar conexión exitosa y cerrar modal automáticamente
  useEffect(() => {
    if (showQRModal && whatsappSocketStatus === 'connected') {
      console.log('[QR] ✅ Conexión detectada, cerrando modal...');
      setQrStatus('scanned');
      setTimeout(() => {
        handleCloseQR();
      }, 2000);
    }
  }, [showQRModal, whatsappSocketStatus]);

  // ───────────────────────────────────────────────────────────────────────
  // FUNCIONES - UTILIDADES
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Obtener color del indicador de estado
   */
  const getStatusColor = (status: 'connected' | 'connecting' | 'disconnected'): string => {
    switch (status) {
      case 'connected': return '#28a745';
      case 'connecting': return '#ffc107';
      case 'disconnected': return '#dc3545';
    }
  };

  /**
   * Obtener texto del indicador de estado
   */
  const getStatusText = (status: 'connected' | 'connecting' | 'disconnected'): string => {
    switch (status) {
      case 'connected': return 'Conectado';
      case 'connecting': return 'Conectando...';
      case 'disconnected': return 'Desconectado';
    }
  };

  /**
   * Indicadores de servicios
   */
  const serviceIndicators: ServiceIndicator[] = [
    {
      name: 'WhatsApp API',
      status: isEndpointAvailable ? 'online' : 'offline',
      icon: faWhatsapp,
      color: isEndpointAvailable ? '#28a745' : '#dc3545'
    },
    {
      name: 'PM2 Service',
      status: isPM2Running ? 'online' : 'offline',
      icon: faCog,
      color: isPM2Running ? '#28a745' : '#dc3545'
    },
    {
      name: mlProvider ? `ML (${mlProvider})` : 'ML Service',
      status: isMLAvailable ? 'online' : 'offline',
      icon: faBrain,
      color: isMLAvailable ? '#28a745' : '#dc3545'
    }
  ];

  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando información del usuario...</p>
      </div>
    );
  }

  return (
    <div className="wasp-dashboard-container" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '20px 24px',
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '2px solid var(--bs-border-color)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: '2rem', color: '#25d366' }} />
            <div>
              <h4 className="mb-0" style={{ fontWeight: 600, color: 'var(--bs-body-color)' }}>
                WASP Dashboard
              </h4>
              <small className="text-muted">v1.0.1 - Sistema de Gestión WhatsApp</small>
            </div>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            {/* Estado de conexión */}
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(whatsappSocketStatus),
                  animation: whatsappSocketStatus === 'connecting' ? 'pulse 1.5s infinite' : 'none'
                }}
              />
              <span style={{ fontSize: '.9rem', color: 'var(--bs-secondary)' }}>
                {getStatusText(whatsappSocketStatus)}
              </span>
            </div>

            {/* Botón QR */}
            <Button
                variant="primary"
                size="sm"
                onClick={handleOpenQR}
                disabled={whatsappSocketStatus === 'connected'}
                style={{
                  borderRadius: '8px',
                  padding: '8px 16px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: whatsappSocketStatus === 'connected' ? 0.5 : 1,
                  cursor: whatsappSocketStatus === 'connected' ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faQrcode} className="me-2" />
                {whatsappSocketStatus === 'connected' ? 'Conectado! ' : 'Escanear QR'}
              </Button>

            {/* Menú de Configuración */}
            <Dropdown 
              show={showConfigDropdown}
              onToggle={(isOpen) => setShowConfigDropdown(isOpen)}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                style={{ borderRadius: '8px' }}
              >
                <FontAwesomeIcon icon={faCog} />
              </Dropdown.Toggle>

              <Dropdown.Menu align="end">
                {/* Opción 1: ESTADO - Ícono señal satelital */}
                <Dropdown.Item 
                  onClick={() => {
                    fetchWaspStatus();
                    setShowConfigDropdown(false);
                  }}
                  disabled={loadingStatus}
                >
                  <FontAwesomeIcon 
                    icon={faBroadcastTower} 
                    className="me-2" 
                    style={{ 
                      color: '#007bff',
                      animation: loadingStatus ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }} 
                  />
                  {loadingStatus ? 'Actualizando...' : 'Estado'}
                </Dropdown.Item>

                <Dropdown.Divider />

                {/* Opción 2: LINKHUB - Ícono networking */}
                <Dropdown.Item 
                  onClick={() => {
                    handleOpenLinkHub();
                    setShowConfigDropdown(false);
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faNetworkWired} 
                    className="me-2" 
                    style={{ color: '#28a745' }} 
                  />
                  LinkHub
                </Dropdown.Item>

                <Dropdown.Divider />

                {/* Opción 3: LOGOUT - Ícono cerradura, bloqueado si desconectado */}
                <Dropdown.Item 
                  onClick={() => {
                    setShowConfigDropdown(false);
                    handleLogout();
                  }}
                  disabled={whatsappSocketStatus === 'disconnected' || sendingCode}
                  style={{
                    color: whatsappSocketStatus === 'disconnected' ? '#6c757d' : '#dc3545'
                  }}
                >
                  {sendingCode ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando código...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon 
                        icon={faLock} 
                        className="me-2" 
                        style={{ 
                          color: whatsappSocketStatus === 'disconnected' ? '#6c757d' : '#dc3545'
                        }} 
                      />
                      {whatsappSocketStatus === 'disconnected' 
                        ? 'Cerrar Sesión (Desconectado)' 
                        : 'Cerrar Sesión'}
                    </>
                  )}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav
          variant="tabs"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            padding: '0 24px',
            borderBottom: '1px solid var(--bs-border-color)'
          }}
        >
          <Nav.Item>
            <Nav.Link
              eventKey="dashboard"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'dashboard' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Dashboard
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="conversations"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'conversations' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faComments} className="me-2" />
              Conversaciones
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="flows"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'flows' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faStream} className="me-2" />
              Gestión de Flujos
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="copilot"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'copilot' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faRobot} className="me-2" />
              Configuración IA
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="applications"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'applications' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              Aplicaciones
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link
              eventKey="config"
              style={{
                fontWeight: 500,
                padding: '12px 20px',
                color: activeTab === 'config' ? '#007bff' : 'var(--bs-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faCog} className="me-2" />
              Configuración
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--bs-body-bg)' }}>
          <Tab.Content style={{ height: '100%' }}>
            {/* TAB 1: DASHBOARD */}
            <Tab.Pane eventKey="dashboard" style={{ height: '100%' }}>
              <DashboardStatsTab />
            </Tab.Pane>

            {/* TAB 2: CONVERSACIONES */}
            <Tab.Pane eventKey="conversations" style={{ height: '100%' }}>
              <ConversationsTable language="es" refreshInterval={5000} copilotEnabled={true} />
            </Tab.Pane>

            {/* TAB 3: GESTIÓN DE FLUJOS */}
            <Tab.Pane eventKey="flows" style={{ height: '100%', overflow: 'auto' }}>
              <div style={{ padding: '24px' }}>
                <DirectivesTable language={'es'} />
              </div>
            </Tab.Pane>

            {/* TAB 4: CONFIGURACIÓN IA */}
            <Tab.Pane eventKey="copilot" style={{ height: '100%', overflow: 'auto' }}>
              <div style={{ padding: '24px' }}>
                <DirectivesCopilot language={'es'} />
              </div>
            </Tab.Pane>

            {/* TAB 5: APLICACIONES */}
            <Tab.Pane eventKey="applications" style={{ height: '100%' }}>
              <ApplicationsTab language="es" />
            </Tab.Pane>

            {/* TAB 6: CONFIGURACIÓN */}
            <Tab.Pane eventKey="config" style={{ height: '100%' }}>
              <ConfigurationTab
                user={user}
                isWhatsAppEnabled={isWhatsAppEnabled}
                isDirectivesEnabled={isDirectivesEnabled}
                isCoPilotEnabled={isCoPilotEnabled}
                loadingToggles={loadingToggles}
                serviceIndicators={serviceIndicators}
                errorMessage={errorMessage}
                successMessage={successMessage}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                handleToggleWhatsApp={handleToggleWhatsApp}
                handleToggleDirectives={handleToggleDirectives}
                handleToggleCoPilot={handleToggleCoPilot}
                hasWsFeatureEnabled={hasWsFeatureEnabled}
              />
            </Tab.Pane>
          </Tab.Content>
        </div>
      </Tab.Container>

      {/* Modal QR - Compacto con Drag */}
      <Modal 
        show={showQRModal} 
        onHide={handleCloseQR}
        centered
        backdrop="static"
        dialogClassName="qr-modal-compact"
      >
        <Modal.Header 
          closeButton
          style={{
            borderBottom: '1px solid var(--bs-border-color)',
            padding: '12px 20px',
            backgroundColor: 'var(--bs-body-bg)',
            cursor: 'move'
          }}
        >
          <Modal.Title style={{ 
            fontWeight: 500, 
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FontAwesomeIcon icon={faQrcode} style={{ color: '#007bff', fontSize: '0.9rem' }} />
            Conectar WhatsApp
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ 
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 'auto'
        }}>
          {/* Logo marca de agua */}
          <img 
            src={wasplogo}
            alt="WASP Watermark"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '280px',
              height: '280px',
              opacity: 0.03,
              pointerEvents: 'none',
              zIndex: 0,
              objectFit: 'contain'
            }}
          />
          
          {/* Contenido */}
          <div style={{ position: 'relative', zIndex: 1, width: '100%', textAlign: 'center' }}>
            {qrStatus === 'loading' && (
              <div style={{ padding: '40px 20px' }}>
                <Spinner 
                  animation="border" 
                  variant="primary" 
                  style={{ width: '50px', height: '50px', marginBottom: '16px' }}
                />
                <p style={{ color: 'var(--bs-secondary)', fontSize: '0.9rem', margin: 0 }}>
                  Generando código QR...
                </p>
              </div>
            )}

            {qrStatus === 'ready' && qrImageData && (
              <>
                {/* QR Container */}
                <div 
                  style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    marginBottom: '16px',
                    position: 'relative',
                    display: 'inline-block'
                  }}
                >
                  <img
                    src={qrImageData}
                    alt="WhatsApp QR Code"
                    style={{
                      width: '240px',
                      height: '240px',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                  
                  {/* Logo WASP en centro */}
                  <img
                    src={wasplogo}
                    alt="WASP Logo"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '60px',
                      height: '60px',
                      background: 'white',
                      borderRadius: '8px',
                      padding: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      objectFit: 'contain'
                    }}
                  />
                </div>

                {/* Instrucciones compactas */}
                <div style={{ maxWidth: '320px', margin: '0 auto' }}>
                  <p style={{ 
                    color: 'var(--bs-body-color)', 
                    marginBottom: '10px',
                    fontWeight: 500,
                    fontSize: '0.9rem'
                  }}>
                    Escanea con WhatsApp
                  </p>
                  
                  <ol style={{ 
                    textAlign: 'left', 
                    color: 'var(--bs-secondary)',
                    fontSize: '0.8rem',
                    lineHeight: '1.5',
                    paddingLeft: '18px',
                    marginBottom: '12px'
                  }}>
                    <li>Abre WhatsApp → <strong>Menú</strong></li>
                    <li><strong>Dispositivos vinculados</strong></li>
                    <li><strong>Vincular dispositivo</strong></li>
                    <li>Apunta a esta pantalla</li>
                  </ol>

                  {/* Timer compacto */}
                  <div style={{
                    padding: '8px 14px',
                    background: 'rgba(0,123,255,0.08)',
                    borderRadius: '6px',
                    border: '1px solid rgba(0,123,255,0.15)'
                  }}>
                    <p style={{ 
                      margin: 0,
                      fontSize: '0.8rem',
                      color: '#007bff',
                      fontWeight: 500
                    }}>
                      ⏱️ Cierra en <strong>{qrAutoCloseTimer}s</strong>
                    </p>
                  </div>
                </div>
              </>
            )}

            {qrStatus === 'scanned' && (
              <div style={{ padding: '30px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
                <h5 style={{ 
                  color: '#28a745', 
                  marginBottom: '8px', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  ¡Conectado!
                </h5>
                <p style={{ color: 'var(--bs-secondary)', fontSize: '0.85rem', margin: 0 }}>
                  Cerrando...
                </p>
              </div>
            )}

            {qrStatus === 'expired' && (
              <div style={{ padding: '30px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>⏰</div>
                <h5 style={{ 
                  color: '#dc3545', 
                  marginBottom: '8px', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  Código expirado
                </h5>
                <p style={{ 
                  color: 'var(--bs-secondary)', 
                  marginBottom: '16px',
                  fontSize: '0.85rem'
                }}>
                  Cierra e intenta nuevamente
                </p>
                <Button 
                  variant="primary" 
                  onClick={handleCloseQR}
                  size="sm"
                  style={{ borderRadius: '6px', padding: '8px 20px', fontSize: '0.85rem' }}
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        
        <Modal.Footer style={{ 
          borderTop: '1px solid var(--bs-border-color)',
          padding: '10px 20px',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseQR}
            size="sm"
            style={{ 
              borderRadius: '6px', 
              padding: '6px 16px',
              fontSize: '0.8rem',
              fontWeight: 500
            }}
          >
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal de Confirmación de Logout */}
      <Modal
        show={showLogoutModal}
        onHide={handleCancelLogout}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2 text-danger" />
            Confirmar Cierre de Sesión
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔐</div>
          
          <p style={{ fontSize: '1rem', color: 'var(--bs-secondary)', marginBottom: '20px' }}>
            Hemos enviado un código de verificación de <strong>6 dígitos</strong> a tu WhatsApp.
          </p>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>
              Ingresa el código recibido
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="000000"
              value={userInputCode}
              onChange={(e) => {
                // Solo permitir números y máximo 6 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setUserInputCode(value);
                setCodeError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && userInputCode.length === 6) {
                  handleVerifyLogoutCode();
                }
              }}
              disabled={verifyingCode}
              isInvalid={!!codeError}
              maxLength={6}
              style={{
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                fontWeight: 'bold',
                padding: '12px',
                borderRadius: '8px'
              }}
              autoFocus
            />
            {codeError && (
              <Form.Control.Feedback type="invalid" style={{ textAlign: 'center', marginTop: '8px' }}>
                {codeError}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <div style={{
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            borderLeft: '4px solid #ffc107',
            padding: '12px 16px',
            borderRadius: '8px',
            textAlign: 'left',
            marginTop: '20px'
          }}>
            <p style={{ 
              fontSize: '0.85rem', 
              color: '#856404',
              margin: 0,
              fontWeight: 500
            }}>
              ⚠️ El código expira en 2 minutos. Si no lo recibiste, cierra este modal e intenta nuevamente.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ 
          border: 'none', 
          padding: '16px 24px',
          backgroundColor: 'var(--bs-light)',
          gap: '12px'
        }}>
          <Button 
            variant="outline-secondary" 
            onClick={handleCancelLogout}
            disabled={verifyingCode}
            style={{
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: 500,
              flex: 1
            }}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleVerifyLogoutCode}
            disabled={verifyingCode || userInputCode.length !== 6}
            style={{
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: 500,
              flex: 1
            }}
          >
            {verifyingCode ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Verificando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                Confirmar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

{/* Estilos globales */}
<style>{`
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .wasp-dashboard-container {
    background-color: var(--bs-body-bg);
    color: var(--bs-body-color);
  }

  .nav-tabs {
    background-color: var(--bs-light);
    border-bottom: 1px solid var(--bs-border-color);
  }

  .nav-tabs .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
    color: var(--bs-secondary);
  }

  .nav-tabs .nav-link.active {
    background-color: transparent !important;
    border-bottom: 2px solid #007bff !important;
    color: #007bff !important;
  }

  .nav-tabs .nav-link:hover {
    border-bottom: 2px solid #007bff;
    color: #007bff;
  }

  .form-switch .form-check-input {
    width: 3rem;
    height: 1.5rem;
    cursor: pointer;
  }

  .form-switch .form-check-input:checked {
    background-color: #28a745;
    border-color: #28a745;
  }

  .form-switch .form-check-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Cards adaptables */
  .card {
    background-color: var(--bs-body-bg);
    border-color: var(--bs-border-color);
  }

  .card-header {
    background-color: var(--bs-body-bg);
    border-color: var(--bs-border-color);
    color: var(--bs-body-color);
  }

  .card-body {
    background-color: var(--bs-body-bg);
    color: var(--bs-body-color);
  }

  /* Modal adaptable */
  .modal-content {
    background-color: var(--bs-body-bg);
    color: var(--bs-body-color);
    border-color: var(--bs-border-color);
  }

  .modal-header {
    background-color: var(--bs-body-bg);
    border-color: var(--bs-border-color);
    color: var(--bs-body-color);
  }

  .modal-body {
    background-color: var(--bs-body-bg);
    color: var(--bs-body-color);
  }

  .modal-footer {
    background-color: var(--bs-light);
    border-color: var(--bs-border-color);
  }

  /* Alerts adaptables */
  .alert {
    border-color: var(--bs-border-color);
  }

  /* Texto secundario */
  .text-muted {
    color: var(--bs-secondary) !important;
  }

  /* Smooth transitions */
  * {
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  }

  /* Scrollbar adaptable al tema */
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: var(--bs-light);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--bs-secondary);
    border-radius: 6px;
    opacity: 0.5;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--bs-body-color);
    opacity: 0.7;
  }

  /* Ajuste para spinner en dark mode */
  .spinner-border {
    color: var(--bs-primary);
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Estilos para el dropdown de configuración */
  .dropdown-menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border: 1px solid var(--bs-border-color);
    background-color: var(--bs-body-bg);
    min-width: 220px;
  }

  .dropdown-item {
    color: var(--bs-body-color);
    padding: 10px 16px;
    transition: all 0.2s ease;
  }

  .dropdown-item:hover:not(:disabled) {
    background-color: var(--bs-light);
    color: var(--bs-body-color);
  }

  .dropdown-item:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .dropdown-divider {
    border-color: var(--bs-border-color);
  }

  /* Modal QR Responsive */
  .qr-modal-custom .modal-dialog {
    max-width: min(550px, 90vw) !important;
    margin: 1.75rem auto;
  }

  @media (max-width: 576px) {
    .qr-modal-custom .modal-dialog {
      margin: 0.5rem;
      max-width: calc(100vw - 1rem) !important;
    }
    
    .qr-modal-body {
      padding: 20px 15px !important;
    }
  }

  .qr-modal-container {
    transition: all 0.3s ease;
  }

  .qr-code-container {
    transition: transform 0.2s ease;
  }

  .qr-code-container:hover {
    transform: scale(1.02);
  }

  /* Modal QR Compacto */
  .qr-modal-compact .modal-dialog {
    max-width: 420px !important;
    margin: 1.75rem auto;
  }

  .qr-modal-compact .modal-content {
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  /* Drag & Drop en header */
  .qr-modal-compact .modal-header {
    user-select: none;
  }

  .qr-modal-compact .modal-header:active {
    cursor: grabbing;
  }

  /* Responsive */
  @media (max-width: 576px) {
    .qr-modal-compact .modal-dialog {
      margin: 0.5rem;
      max-width: calc(100vw - 1rem) !important;
    }

    .qr-modal-compact .modal-body img[alt="WhatsApp QR Code"] {
      width: 200px !important;
      height: 200px !important;
    }

    .qr-modal-compact .modal-body img[alt="WASP Logo"] {
      width: 50px !important;
      height: 50px !important;
    }
  }
`}</style>
    </div>
  );
};

export default WaspDashboard;